/**
 * Where each of the 100 level nodes sits on the winding mountain path.
 *
 * Pure logic: no Phaser imports. Ten levels per screen-tall region, x weaving
 * inside the safe area, y strictly decreasing, so the map is one tall column the
 * camera scrolls (CLAUDE.md).
 */
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT } from '../types';

export const LEVELS_PER_REGION = 10;
export const NODE_SPACING_Y = 112;

/** The lowest node sits here, just above the map's footer. */
export const MAP_BASE_Y = GAME_HEIGHT - 150;

/** Node centres stay inside this much of the stage width. */
export const MAP_X_MARGIN = IS_PORTRAIT ? 210 : 360;

/** Taller on the tall stage, where the buttons get a row of their own. */
export const MAP_HEADER_HEIGHT = IS_PORTRAIT ? 168 : 108;

export interface MapNode {
  readonly level: number;
  readonly x: number;
  readonly y: number;
  /** 0-based band of ten, for alternating the weave and tinting regions. */
  readonly region: number;
}

/** Level 1 at the bottom, level 100 at the top. */
export function levelNodePosition(level: number): MapNode {
  // Math.round(NaN) is NaN and would survive a bare clamp, so guard first.
  const safe = Number.isFinite(level) ? Math.round(level) : 1;
  const index = Math.max(1, Math.min(100, safe)) - 1;
  const region = Math.floor(index / LEVELS_PER_REGION);
  const withinRegion = index % LEVELS_PER_REGION;

  // Weave across the stage: one full S per region, every region the same way
  // round, so the trail carries straight on from one world into the next as a
  // single S all the way up. (Mirroring alternate regions made the trail turn
  // back on itself at every world boundary.)
  const phase = (withinRegion / LEVELS_PER_REGION) * Math.PI * 2;
  const amplitude = (GAME_WIDTH - MAP_X_MARGIN * 2) / 2;

  return {
    level: index + 1,
    x: GAME_WIDTH / 2 + Math.sin(phase) * amplitude,
    y: MAP_BASE_Y - index * NODE_SPACING_Y,
    region,
  };
}

export function allLevelNodes(maxLevel = 100): MapNode[] {
  return Array.from({ length: maxLevel }, (_, i) => levelNodePosition(i + 1));
}

/**
 * The vertical slice of the map that belongs to one world (ten levels).
 *
 * Slices meet halfway between a world's last node and the next world's first,
 * and the lowest and highest stretch to the map's edges, so the whole map is
 * covered with no gaps or overlaps.
 */
export function regionSpan(region: number, maxLevel = 100): { top: number; bottom: number } {
  const regions = Math.ceil(maxLevel / LEVELS_PER_REGION);
  const r = Math.max(0, Math.min(regions - 1, Math.round(region)));
  const half = NODE_SPACING_Y / 2;
  const firstIndex = r * LEVELS_PER_REGION;
  const lastIndex = Math.min(maxLevel, firstIndex + LEVELS_PER_REGION) - 1;
  const bounds = mapBounds(maxLevel);
  const bottom =
    r === 0 ? bounds.y + bounds.height : MAP_BASE_Y - firstIndex * NODE_SPACING_Y + half;
  const top = r === regions - 1 ? bounds.y : MAP_BASE_Y - lastIndex * NODE_SPACING_Y - half;
  return { top, bottom };
}

/**
 * The tip of the summit peak: just up and to the side of the last level, so
 * level 100 sits on the peak beside the South Pole planted at its tip.
 */
export function summitPoint(maxLevel = 100): { x: number; y: number } {
  const last = levelNodePosition(maxLevel);
  const toward = last.x < GAME_WIDTH / 2 ? 1 : -1;
  return { x: last.x + toward * 110, y: last.y - 40 };
}

export interface MapBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Camera bounds covering the whole trail plus room for the header and footer. */
export function mapBounds(maxLevel = 100): MapBounds {
  // Room above the last level for the summit and the South Pole on it.
  const top = levelNodePosition(maxLevel).y - 320;
  const bottom = MAP_BASE_Y + 150;
  return { x: 0, y: top, width: GAME_WIDTH, height: bottom - top };
}

/** Scroll position that frames a given level comfortably. */
export function scrollYForLevel(level: number, maxLevel = 100): number {
  const node = levelNodePosition(level);
  const bounds = mapBounds(maxLevel);
  const target = node.y - GAME_HEIGHT / 2;
  const lowest = bounds.y;
  const highest = bounds.y + bounds.height - GAME_HEIGHT;
  return Math.min(highest, Math.max(lowest, target));
}
